/**
 * Bundle entry for `ls-plugin.js` — the file plugin authors load with a script
 * tag. Constructing the client attaches the message listener and sends `ready`,
 * so this must run as early as possible in the plugin document.
 *
 *   <script src="https://cdn.jsdelivr.net/npm/@livespace/plugin-sdk@1/dist/ls-plugin.js"></script>
 *
 * Published to npm as `@livespace/plugin-sdk`; authors load `dist/` from any
 * mirror of the package. See `RELEASING.md`, and ls-fe's
 * `micro-apps/plugin-slot/docs/plugin-contract.md` §0.
 *
 * The SDK works out the Livespace origin from the embedding chain
 * (`ancestorOrigins`, falling back to `document.referrer`). In the rare browser
 * where neither is available, name it on the script tag:
 *
 *   <script src="…/ls-plugin.js" data-host-origin="https://app.livespace.io"></script>
 */

import { LsPluginClient } from "./ls-plugin.js"

const hostOrigin = (document.currentScript as HTMLScriptElement | null)?.dataset
	?.hostOrigin

const client = new LsPluginClient(hostOrigin ? { hostOrigin } : {})

// A global `var`, not a `Window` member: that is what types the bare
// `LsPlugin.on(…)` every example uses, and `window.LsPlugin` with it. Authors
// get it from `@livespace/plugin-sdk/global` — see `dist-types.test.ts`.
declare global {
	var LsPlugin: LsPluginClient
}

/**
 * A DEFAULT export, and the only export — which is what actually puts the client
 * on `window.LsPlugin`.
 *
 * Rollup's iife wrapper emits `var LsPlugin = (function(){ … })()`, and at the top
 * level of a classic script that `var` *is* `window.LsPlugin`. So whatever this
 * module returns becomes the global, and it silently overwrites anything the
 * module body assigned to `window.LsPlugin` itself.
 *
 * With a named export the returned value is the exports namespace, so the global
 * ends up as `{ LsPlugin: client }` and every documented call — `LsPlugin
 * .createConfigForm()` — is a TypeError. Pairing this with
 * `output.exports: "default"` in the vite config makes the returned value the
 * client itself. The two settings only work together; changing one without the
 * other breaks the global. See `dist-global.test.ts`, which asserts the built
 * artifact rather than this source.
 */
export default client
